import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Gate } from '../../../global/contexts/auth/auth';
import { formatDateOnly } from '../../../common/utils/date';
import { fmtMoney } from '../../../common/utils/money';
import { useAccountStatement } from '../contexts/accounts';
import PaymentForm from '../forms/payment-form';

import '../styles/billing.css';

// Estado de cuenta de un cliente (ledger, admin-billing-ledger.md): debe/haber/saldo
// del subledger owner_account + movimientos. saldo > 0 = el dueño DEBE. Registrar pago
// desde acá. Los montos vienen del backend (doble-partida); el front solo muestra.
const SOURCE_LABEL = {
    liquidation:  'Pensión',
    sale:         'Venta',
    exchange:     'Ajuste',
    payment:      'Pago',
    credit_note:  'Nota crédito',
};

export default function AccountStatement({ clientId }) {
    const [range, setRange] = useState({ from: '', to: '' });
    const [paying, setPaying] = useState(false);

    const filters = useMemo(() => {
        const out = {};
        if (range.from) out.from = range.from;
        if (range.to) out.to = range.to;
        return out;
    }, [range]);

    const { data, isLoading, isFetching, error } = useAccountStatement(clientId, filters);
    const client = data?.client;
    const totals = data?.totals || {};
    const bySource = data?.by_source || {};
    const activity = data?.activity || [];
    const balance = Number(totals.balance) || 0;

    return (
        <div className={`billing-liq ${isFetching ? 'billing-liq--busy' : ''}`}>
            <header className="billing-liq__header">
                <div>
                    <Link to="/admin/clients" className="billing-liq__back">Clientes</Link>
                    <h1 className="billing-liq__title">Estado de cuenta · {client?.name || `#${clientId}`}</h1>
                </div>
                <div className="billing-liq__period">
                    <input type="date" value={range.from} max={range.to || undefined} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} disabled={isFetching} />
                    <input type="date" value={range.to} min={range.from || undefined} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} disabled={isFetching} />
                    <Gate domain="billing" action="write">
                        <button type="button" className="billing-btn billing-btn--primary" onClick={() => setPaying(true)}>Registrar pago</button>
                    </Gate>
                </div>
            </header>

            {error && <div className="cat-overview__error">{error.status === 403 ? 'No tenés permiso para ver el estado de cuenta.' : (error.message || 'No se pudo cargar el estado de cuenta')}</div>}

            {isLoading && !data ? (
                <div className="cat-overview__state">Cargando estado de cuenta…</div>
            ) : (
                <>
                    <div className={`billing-liq__total-card ${balance > 0 ? 'billing-liq__total-card--debt' : ''}`}>
                        <span className="billing-liq__total-label">{balance > 0 ? 'Saldo (debe)' : balance < 0 ? 'Saldo a favor' : 'Saldo'}</span>
                        <span className="billing-liq__total-value">{fmtMoney(Math.abs(balance))}</span>
                        <span className="billing-liq__total-break">Cargos {fmtMoney(totals.debit)} · Abonos {fmtMoney(totals.credit)}</span>
                    </div>

                    {Object.keys(bySource).length > 0 && (
                        <div className="billing-account__sources">
                            {Object.entries(bySource).map(([src, v]) => (
                                <div key={src} className="billing-account__source">
                                    <span className="billing-account__source-label">{SOURCE_LABEL[src] || src}</span>
                                    <span className="billing-account__source-val">{fmtMoney((Number(v.debit) || 0) - (Number(v.credit) || 0))}</span>
                                </div>
                            ))}
                        </div>
                    )}

                    <section className="billing-liq__section">
                        <h2 className="billing-liq__section-title">Movimientos</h2>
                        {!activity.length ? (
                            <p className="billing-liq__hint">Sin movimientos en el período.</p>
                        ) : (
                            <div className="cat-overview__card">
                                <table className="cat-table cat-table--compact">
                                    <thead><tr><th>Fecha</th><th>Concepto</th><th>Origen</th><th>Débito</th><th>Crédito</th></tr></thead>
                                    <tbody>
                                        {activity.map((a) => (
                                            <tr key={a.entry_id} className="cat-table__row">
                                                <td>{formatDateOnly(a.entry_date) || '—'}</td>
                                                <td style={{ textAlign: 'left' }}>{a.description || '—'}</td>
                                                <td>{SOURCE_LABEL[a.source_type] || a.source_type}</td>
                                                <td>{a.direction === 'debit' ? fmtMoney(a.amount) : ''}</td>
                                                <td>{a.direction === 'credit' ? fmtMoney(a.amount) : ''}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </section>
                </>
            )}

            {paying && <PaymentForm clientId={clientId} clientName={client?.name} onClose={() => setPaying(false)} onSaved={() => setPaying(false)} />}
        </div>
    );
}
